/**
 * App toast helpers — one place for auth / MFA copy and post-redirect flashes.
 * Visuals live in `@w7/ui` ToastProvider (top-right, slide-in, progress bar).
 */

import { useCallback } from 'react'
import { useToast, type ToastTone } from '@w7/ui'

const FLASH_KEY = 'w7.toast.flash'

export type ToastFlash = {
  tone: ToastTone
  message: string
  detail?: string
}

/** Survives full-page redirects (OIDC logout → /login, callback → app). */
export function flashToast(toast: ToastFlash): void {
  try {
    sessionStorage.setItem(FLASH_KEY, JSON.stringify(toast))
  } catch {
    /* private mode / quota — toast is best-effort */
  }
}

export function consumeToastFlash(): ToastFlash | null {
  try {
    const raw = sessionStorage.getItem(FLASH_KEY)
    if (!raw) return null
    sessionStorage.removeItem(FLASH_KEY)
    return JSON.parse(raw) as ToastFlash
  } catch {
    sessionStorage.removeItem(FLASH_KEY)
    return null
  }
}

export const toastCopy = {
  loginSuccess: {
    tone: 'success' as const,
    message: 'Signed in successfully',
    detail: 'Welcome back to the ops console.',
  },
  logoutSuccess: {
    tone: 'success' as const,
    message: 'Signed out successfully',
    detail: 'Your session has ended.',
  },
  loginExpired: {
    tone: 'warn' as const,
    message: 'Session expired',
    detail: 'Sign in again to continue.',
  },
  sessionIdle: {
    tone: 'warn' as const,
    message: 'Signed out due to inactivity',
    detail: 'No activity for 30 minutes. Sign in again to continue.',
  },
  sessionMax: {
    tone: 'warn' as const,
    message: 'Session time limit reached',
    detail: 'Login sessions last 1 hour. Sign in again to continue.',
  },
  loginDenied: {
    tone: 'warn' as const,
    message: 'Sign-in cancelled',
    detail: 'No session was created.',
  },
  loginError: {
    tone: 'error' as const,
    message: 'Sign-in failed',
    detail: 'Try again, or use demo access if available.',
  },
  mfaEnrolled: (label: string, detail?: string) => ({
    tone: 'success' as const,
    message: `${label} is active`,
    detail: detail ?? 'Next sign-in can ask for this second factor.',
  }),
  mfaRemoved: {
    tone: 'success' as const,
    message: 'Device removal finished',
    detail:
      'If Keycloak confirmed the delete, that factor is gone. Status refreshes automatically — remove any remaining devices the same way.',
  },
  mfaRemovedAll: {
    tone: 'success' as const,
    message: 'Device removal finished',
    detail:
      'If Keycloak confirmed the delete, every factor is gone. Status refreshes automatically.',
  },
  mfaRemoveError: (detail?: string) => ({
    tone: 'error' as const,
    message: 'Could not remove this factor',
    detail: detail ?? 'Refresh status, then try again.',
  }),
  mfaEnrollError: (detail?: string) => ({
    tone: 'error' as const,
    message: 'Could not start 2FA setup',
    detail: detail ?? 'Try again.',
  }),
  mfaNeedKeycloak: {
    tone: 'info' as const,
    message: 'Sign in with Keycloak',
    detail: 'Demo sessions cannot manage MFA devices.',
  },
  passwordNeedKeycloak: {
    tone: 'info' as const,
    message: 'Sign in with Keycloak',
    detail: 'Demo sessions cannot change the Keycloak password.',
  },
  passwordChanged: {
    tone: 'success' as const,
    message: 'Password updated',
    detail: 'Use the new password the next time you sign in.',
  },
  passwordCancelled: {
    tone: 'info' as const,
    message: 'Password change cancelled',
    detail: 'Your existing password is unchanged.',
  },
  passwordChangeIncomplete: {
    tone: 'warn' as const,
    message: 'Password was not changed',
    detail:
      'Keycloak did not finish UPDATE_PASSWORD. Re-seed local IdP (npm run keycloak:seed) or try again.',
  },
  passwordChangeError: (detail?: string) => ({
    tone: 'error' as const,
    message: 'Could not open change password',
    detail: detail ?? 'Try again, or sign in with Keycloak first.',
  }),
  mfaNotConfigured: {
    tone: 'warn' as const,
    message: 'Sign-in is not configured',
    detail: 'Set VITE_KEYCLOAK_URL first.',
  },
  mfaPasskeyHost: {
    tone: 'error' as const,
    message: 'Passkeys need localhost, not an IP address',
    detail:
      'Fingerprint / passkey registration fails on 127.0.0.1. Use http://localhost:5273 and VITE_KEYCLOAK_URL=http://localhost:8081. Authenticator app still works on either host.',
  },
  marketCreated: {
    tone: 'success' as const,
    message: 'Market created',
  },
  marketUpdated: {
    tone: 'success' as const,
    message: 'Market updated',
  },
  marketSaveError: (isEdit: boolean, detail: string) => ({
    tone: 'error' as const,
    message: isEdit ? 'Market update failed' : 'Market create failed',
    detail,
  }),
} as const

/** Typed helpers on top of `useToast` — prefer this in features. */
export function useAppToast() {
  const toast = useToast()

  const show = useCallback(
    (item: ToastFlash) => {
      toast.push(item)
    },
    [toast],
  )

  return {
    ...toast,
    show,
    loginSuccess: () => show(toastCopy.loginSuccess),
    logoutSuccess: () => show(toastCopy.logoutSuccess),
    mfaEnrolled: (label: string, detail?: string) => show(toastCopy.mfaEnrolled(label, detail)),
    mfaRemoved: () => show(toastCopy.mfaRemoved),
    mfaRemovedAll: () => show(toastCopy.mfaRemovedAll),
    mfaRemoveError: (detail?: string) => show(toastCopy.mfaRemoveError(detail)),
    mfaEnrollError: (detail?: string) => show(toastCopy.mfaEnrollError(detail)),
  }
}
